import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, ArrowLeft, Loader2 } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterHeaderCard, masterHeaderIcon, masterInput, masterInputLabel,
  masterPageShell, masterSearchWrap, masterSearchInput, masterToolbar, masterSelect, masterSubtitle, masterTitle,
  masterAccentButton, masterPrimaryButton, masterTableData
} from '../components/masterPageStyles';

const TYPE_CACHE_KEY = 'type-master-options';

const TypeMaster = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(true); // Default to form page view on mount
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({ typeName: '', status: 'Active' });
  // 🟢 ADD THESE STATES (below searchTerm state):
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

// 🟢 ADD THESE HOOKS BEFORE THE return STATEMENT:
  const filteredData = useMemo(() => {
    const sortedList = [...data].sort((a, b) => {
      const idA = String(a.typeId || a._id || '');
      const idB = String(b.typeId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const typeName = (item.typeName || '').toLowerCase();
      const typeId = (item.typeId || '').toLowerCase();

      return typeName.startsWith(q) || typeId.startsWith(q);
    });
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const syncTypeCache = (records) => {
    if (typeof window === 'undefined') return;
    const normalized = (records || [])
      .filter((item) => String(item.status || 'Active') === 'Active')
      .map((item) => ({
        value: String(item.typeName || '').toUpperCase(),
        label: String(item.typeName || '').toUpperCase()
      }));
    localStorage.setItem(TYPE_CACHE_KEY, JSON.stringify(normalized));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/master/type/all');
      if (res.data.success) {
        const records = res.data.data || [];
        setData(records);
        syncTypeCache(records);
      }
    } catch (err) { 
      toast.error('Failed to load types'); 
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    fetchData(); 
  }, []);

  const handleOpenForm = (item = null) => {
    if (item) {
      setEditId(item._id);
      setFormData({ typeName: item.typeName, status: item.status });
    } else {
      setEditId(null);
      setFormData({ typeName: '', status: 'Active' });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(true); // Retain form layout view
    setEditId(null);
    setFormData({ typeName: '', status: 'Active' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const url = editId ? `/master/type/save/${editId}` : '/master/type/save';
      await api.post(url, { ...formData, typeName: formData.typeName.trim().toUpperCase() });
      toast.success('Successfully Saved');
      setEditId(null);
      setFormData({ typeName: '', status: 'Active' });
      setShowForm(true); // Keep interface pinned to form view
      await fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.message || 'Save failed'); 
    } finally { 
      setIsSaving(false); 
    }
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-2">
          <div className={masterHeaderIcon}><Layers size={20} /></div>
          <div>
            <h1 className={masterTitle}>Type Master</h1>
            <p className={masterSubtitle}>General Categorization</p>
          </div>
        </div>
        <button 
          onClick={() => {
            if (showForm) {
              handleCloseForm();
            }
            setShowForm(!showForm);
          }} 
          className={showForm ? masterPrimaryButton : masterAccentButton}
        >
          {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}
          {showForm ? 'View Registered List' : 'Add New Type'}
        </button>
      </header>

      <main className="space-y-2">
        {/* INLINE FORM SELECTION PANEL PANEL */}
        {showForm && (
          <div className="bg-white rounded-xl border border-slate-400 p-1 shadow-sm max-w-full">
            <div className="mb-1">
              <h2 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                {editId ? 'Edit Type Details' : 'Create New Type'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <label className={masterInputLabel}>Type Name</label>
                  <input 
                    autoFocus
                    value={formData.typeName} 
                    onChange={e => setFormData({...formData, typeName: e.target.value})} 
                    required 
                    placeholder="Enter type name..."
                    className={masterInput} 
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className={masterInputLabel}>Status</label>
                  <select 
                    value={formData.status} 
                    onChange={e => setFormData({...formData, status: e.target.value})} 
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button 
                  disabled={isSaving} 
                  type="submit" 
                  className={`${masterAccentButton} px-6 py-1`}
                >
                  {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
                  {editId ? 'Update Changes' : 'Save Type'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* LOG RECORD REGISTERED REGISTRY */}
        {!showForm && (
          <div className="bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm animate-in fade-in duration-200">
            <div className="border-b border-slate-200 flex-shrink-0">
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by type name or ID..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>ID</th>
                    <th className={masterTableData}>Type Name</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-400 font-bold uppercase text-xs">
                        Loading record indices...
                      </td>
                    </tr>
                 ) : paginatedData.length > 0 ? (
                    paginatedData.map((item, index) => (
                      <tr key={item._id} className="hover:bg-slate-50 font-bold text-slate-700 text-[11px]">
                        <td className={masterTableData}>
                          {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className={masterTableData}>{item.typeId}</td>
                        <td className={masterTableData}>{item.typeName}</td>
                        <td className={masterTableData}>
                          <span className={`px-2 py-0.5 rounded-full text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className={masterTableData}>
                          <div className="flex gap-2">
                            <button 
                              onClick={() => handleOpenForm(item)} 
                              className="text-emerald-600 hover:bg-emerald-50 p-1 rounded-lg transition-colors"
                            >
                              <Edit size={14}/>
                            </button>
                            <button 
                              onClick={async () => { 
                                if(window.confirm('Delete this configuration type record?')) { 
                                  await api.delete(`/master/type/${item._id}`); 
                                  await fetchData(); 
                                } 
                              }} 
                              className="text-rose-600 hover:bg-rose-50 p-1 rounded-lg transition-colors"
                            >
                              <Trash2 size={14}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                        No matching records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TypeMaster;